/**
 * RSVP Button Group Component
 * Three mutually exclusive buttons: Attending, Maybe, Can't Go
 */

import { useState } from 'react'
import {
  Box,
  Button,
  ButtonGroup,
  TextField,
  Typography,
  CircularProgress,
  Collapse,
  Alert,
} from '@mui/material'
import {
  CheckCircle,
  HelpOutline,
  Cancel,
  Login,
} from '@mui/icons-material'
import { RsvpStatus, CalendarEventStatus } from '@api/types/calendarEvent'
import { useRsvpToEvent, useRemoveRsvp } from '../hooks/useCalendarEvents'
import { isEventPast } from '../types/calendar-event-editor.types'

interface RsvpButtonGroupProps {
  eventId: string
  eventStatus: CalendarEventStatus | number
  endDateTime: string
  isEventFull: boolean
  currentRsvp?: {
    status: RsvpStatus
    note?: string
  }
  isLoggedIn: boolean
  onLoginRequired?: () => void
}

export function RsvpButtonGroup({
  eventId,
  eventStatus,
  endDateTime,
  isEventFull,
  currentRsvp,
  isLoggedIn,
  onLoginRequired,
}: RsvpButtonGroupProps) {
  const [note, setNote] = useState(currentRsvp?.note || '')
  const [showNote, setShowNote] = useState(!!currentRsvp?.note)
  const [pendingStatus, setPendingStatus] = useState<RsvpStatus | null>(null)

  const rsvpMutation = useRsvpToEvent()
  const removeRsvpMutation = useRemoveRsvp()

  const isPast = isEventPast(endDateTime)
  const isPublished = eventStatus === CalendarEventStatus.Published
  const isCancelled = eventStatus === CalendarEventStatus.Cancelled

  // Determine if RSVP is disabled
  const isDisabled = !isPublished || isPast || isCancelled
  const isLoading = rsvpMutation.isPending || removeRsvpMutation.isPending

  // Get disabled reason
  const getDisabledReason = (): string | null => {
    if (!isLoggedIn) return 'Log in to RSVP'
    if (isCancelled) return 'Event has been cancelled'
    if (isPast) return 'Event has already ended'
    if (!isPublished) return 'Event is not published'
    if (isEventFull && currentRsvp?.status !== RsvpStatus.Attending)
      return 'Event is full'
    return null
  }

  const disabledReason = getDisabledReason()

  // Handle RSVP selection
  const handleRsvp = async (status: RsvpStatus) => {
    if (!isLoggedIn) {
      onLoginRequired?.()
      return
    }

    // If clicking the same status, remove RSVP
    if (currentRsvp?.status === status) {
      try {
        await removeRsvpMutation.mutateAsync(eventId)
        setNote('')
        setShowNote(false)
      } catch (error) {
        console.error('Failed to remove RSVP:', error)
      }
      return
    }

    // Set new RSVP
    setPendingStatus(status)
    try {
      await rsvpMutation.mutateAsync({
        eventId,
        data: {
          status,
          note: note.trim() || undefined,
        },
      })
    } catch (error) {
      console.error('Failed to set RSVP:', error)
    } finally {
      setPendingStatus(null)
    }
  }

  // Handle note update
  const handleNoteUpdate = async () => {
    if (!currentRsvp) return

    try {
      await rsvpMutation.mutateAsync({
        eventId,
        data: {
          status: currentRsvp.status,
          note: note.trim() || undefined,
        },
      })
    } catch (error) {
      console.error('Failed to update note:', error)
    }
  }

  // Render login prompt if not logged in
  if (!isLoggedIn) {
    return (
      <Box>
        <Button
          variant="outlined"
          startIcon={<Login />}
          onClick={onLoginRequired}
          fullWidth
        >
          Log in to RSVP
        </Button>
      </Box>
    )
  }

  return (
    <Box>
      {/* Disabled reason alert */}
      {disabledReason && currentRsvp?.status !== RsvpStatus.Attending && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {disabledReason}
        </Alert>
      )}

      {/* RSVP Buttons */}
      <ButtonGroup
        variant="outlined"
        fullWidth
        disabled={isDisabled || isLoading}
        sx={{ mb: 2 }}
      >
        <Button
          variant={currentRsvp?.status === RsvpStatus.Attending ? 'contained' : 'outlined'}
          color="success"
          onClick={() => handleRsvp(RsvpStatus.Attending)}
          startIcon={
            pendingStatus === RsvpStatus.Attending ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <CheckCircle />
            )
          }
          disabled={isDisabled || (isEventFull && currentRsvp?.status !== RsvpStatus.Attending)}
        >
          Attending
        </Button>
        <Button
          variant={currentRsvp?.status === RsvpStatus.Tentative ? 'contained' : 'outlined'}
          color="warning"
          onClick={() => handleRsvp(RsvpStatus.Tentative)}
          startIcon={
            pendingStatus === RsvpStatus.Tentative ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <HelpOutline />
            )
          }
        >
          Maybe
        </Button>
        <Button
          variant={currentRsvp?.status === RsvpStatus.NotAttending ? 'contained' : 'outlined'}
          color="error"
          onClick={() => handleRsvp(RsvpStatus.NotAttending)}
          startIcon={
            pendingStatus === RsvpStatus.NotAttending ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <Cancel />
            )
          }
        >
          Can't Go
        </Button>
      </ButtonGroup>

      {/* Note section */}
      {currentRsvp && (
        <Box>
          <Typography
            variant="body2"
            color="primary"
            sx={{ cursor: 'pointer', mb: 1 }}
            onClick={() => setShowNote(!showNote)}
          >
            {showNote ? 'Hide note' : currentRsvp.note ? 'Edit note' : 'Add a note'}
          </Typography>

          <Collapse in={showNote}>
            <TextField
              fullWidth
              multiline
              rows={2}
              placeholder="Add an optional note (e.g., dietary restrictions, bringing a guest)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              inputProps={{ maxLength: 500 }}
              sx={{ mb: 1 }}
            />
            {note !== (currentRsvp.note || '') && (
              <Button
                size="small"
                variant="contained"
                onClick={handleNoteUpdate}
                disabled={isLoading}
              >
                Save Note
              </Button>
            )}
          </Collapse>
        </Box>
      )}

      {/* Error display */}
      {(rsvpMutation.isError || removeRsvpMutation.isError) && (
        <Alert severity="error" sx={{ mt: 2 }}>
          Failed to update RSVP. Please try again.
        </Alert>
      )}
    </Box>
  )
}

export default RsvpButtonGroup
